import { motion } from "framer-motion";
import { GraduationCap, BookOpen, Globe, Stethoscope, FlaskConical } from "lucide-react";

const founders = [
  {
    id: "santosh",
    name: "Dr. Santosh More",
    honorific: "Founder",
    portrait:
      "https://customer-assets-cm19k8pv.emergentagent.net/job_wisdom-pulse-3/artifacts/5ohh4aaa_Santosh.jpg",
    objectPos: "center 20%",
    tint: "var(--sunflower)",
    creds: [
      { Icon: GraduationCap, text: "MA Sanskrit · PhD (traditional gurukul-style study)" },
      { Icon: BookOpen, text: "Upaniṣads · Bhagavad Gītā · Brahma Sūtras" },
      { Icon: Globe, text: "Teacher: India · China · Switzerland · Poland · USA" },
    ],
    bio: [
      "Dr. Santosh More came to the United States from Bharat to earn a Master's in Computer Engineering — and found success wasn't enough. Seeking something deeper, he spent his prime years in a monastery, studying the Upaniṣads, the Bhagavad Gītā, and the Brahma Sūtras in the traditional style under India's finest scholars, before completing a Master's in Sanskrit and a PhD.",
      "Today, he leads as Director of Services in the IT industry by day, and teaches the Dnyāneshwarī and Bhagavad Gītā to seekers across India, China, Switzerland, Poland, and the U.S. by calling.",
      "This rare fluency in both worlds — modern complexity and ancient wisdom — is his niche. His mission: that every person, everywhere, should access the knowledge and bliss spirituality offers. Shemushi is where that mission comes alive.",
    ],
    pull: "\u201CHe saw this knowledge growing more urgent, not less — and children turning away simply because no one made it feel alive. Inner Compass is his answer.\u201D",
  },
  {
    id: "navita",
    name: "Dr. Navita More",
    honorific: "Founder",
    portrait:
      "https://customer-assets-cm19k8pv.emergentagent.net/job_wisdom-pulse-3/artifacts/b50skqp1_Navita.jpg",
    objectPos: "center 30%",
    tint: "var(--mint)",
    creds: [
      { Icon: GraduationCap, text: "MD Psychiatry + Homeopathy · Licensed physician (India)" },
      { Icon: Stethoscope, text: "10+ years clinical practice · 1,000+ children reached" },
      { Icon: FlaskConical, text: "Govt-sponsored research · learning disabilities in children" },
    ],
    bio: [
      "Dr. Navita More, founder of Shemushi, is a licensed physician in India with an MD in Psychiatry and Homeopathy, dedicated to transforming how children and families experience mental health care.",
      "Over more than a decade, she has guided over 1,000 children through school mental health programs and supported thousands more through her clinical practice — combining clinical rigor with genuine compassion.",
      "Her government-sponsored research on learning disabilities in children broke new ground, delivering measurable results where conventional approaches fell short. Now pursuing her Master's in Counseling with US licensure on the horizon, she brings a rare blend of medical expertise, research-driven insight, and heartfelt dedication to every child and family she serves.",
    ],
    pull: "\u201CClinical rigor with genuine compassion — that is the room I try to build for every child and family I serve.\u201D",
  },
];

export default function Founders() {
  return (
    <section
      id="founders"
      className="paper-bg py-24 md:py-40 relative overflow-hidden"
      data-testid="founders-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 relative">
        <div className="grid grid-cols-12 gap-6 items-end mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-8">
            <span className="font-mono-editorial text-[11px] text-[color:var(--muted)]">— The Founders</span>
            <h2 className="mt-6 font-display text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tight text-[color:var(--ink)]">
              <span className="italic font-serif-editorial" style={{ color: "var(--brand)" }}>
                A spiritualist.
              </span>
              <br />
              A physician. One shared room.
            </h2>
          </div>
          <p className="col-span-12 md:col-span-4 text-[color:var(--ink)]/75 leading-relaxed self-end">
            Shemushi is not a franchise. Every batch is designed, taught and reviewed by the same two people who founded it. That is the whole quality guarantee.
          </p>
        </div>

        <div className="space-y-16 md:space-y-28">
          {founders.map((f, idx) => {
            const flip = idx % 2 === 1;
            return (
              <motion.article
                key={f.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="grid grid-cols-12 gap-6 items-center"
                data-testid={`founder-${f.id}`}
              >
                {/* Portrait */}
                <div className={`col-span-12 md:col-span-5 ${flip ? "md:order-2" : ""}`}>
                  <div className="relative">
                    <div
                      className="absolute -top-6 -left-6 w-40 h-40 rounded-full hidden md:block"
                      style={{ background: f.tint }}
                      aria-hidden
                    />
                    <div
                      className={`relative overflow-hidden border hairline aspect-[4/5] spotlight ${flip ? "rounded-b-[9999px]" : "rounded-t-[9999px]"}`}
                    >
                      <img
                        src={f.portrait}
                        alt={f.name}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: f.objectPos }}
                        data-testid={`founder-portrait-${f.id}`}
                      />
                    </div>
                  </div>
                </div>

                {/* Text */}
                <div className={`col-span-12 md:col-span-7 md:pl-8 ${flip ? "md:order-1 md:pl-0 md:pr-8" : ""}`}>
                  <span
                    className="inline-block font-mono-editorial text-[10px] tracking-[0.32em] px-3 py-1 rounded-full"
                    style={{ background: f.tint, color: "var(--ink)" }}
                  >
                    {f.honorific.toUpperCase()}
                  </span>
                  <h3 className="mt-4 font-display text-5xl md:text-7xl leading-[0.95] tracking-tight text-[color:var(--ink)]">
                    {f.name}
                  </h3>

                  <div className="mt-6 font-serif-editorial text-[color:var(--ink)]/85 text-base md:text-lg leading-relaxed max-w-[60ch] space-y-4">
                    {f.bio.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>

                  <blockquote
                    className="mt-8 border-l-2 pl-6 font-serif-editorial italic text-2xl md:text-3xl leading-[1.2] text-[color:var(--ink)] max-w-[38ch]"
                    style={{ borderColor: f.tint }}
                  >
                    {f.pull}
                  </blockquote>

                  <ul className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {f.creds.map((c, i) => {
                      const Icon = c.Icon;
                      return (
                        <li
                          key={i}
                          className="border hairline p-4 flex items-start gap-3 paper-bg"
                        >
                          <span
                            className="inline-flex w-9 h-9 rounded-full items-center justify-center flex-shrink-0"
                            style={{ background: f.tint }}
                          >
                            <Icon size={16} color="var(--ink)" strokeWidth={1.75} />
                          </span>
                          <span className="text-sm leading-snug text-[color:var(--ink)]/85">{c.text}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
